import {
  enumeratedLiterals,
  type EnumeratedLiteralType,
} from "@north-beam/nb-common";

export const AmplitudeEventContainers = enumeratedLiterals(
  [
    "top-bar",
    "data-export-menu",
    "saved-views-menu",
    "saved-views-modal",
    "breakdowns-management",
    "tile-management",
    "date-ranges",
    "links",
    "controls",
  ] as const,
  {},
);

/**
 * A representation of where the {@link AmplitudeEvent} occurred on a given {@link Page}, which
 * is a containing element that the event occurred within.  The {@link AmplitudeEventContainer}
 * is combined with specific information about the {@link Page} on whcih the event occurred to
 * construct the {@link AmplitudeEventPath} - which represents the overall location in which the
 * {@link AmplitudeEvent} occurred in the application.
 *
 * The {@link AmplitudeEventContainer} can either be provided as a dynamic option to the
 * {@link Logger} on instantiation, or it can be provided in the context that is supplied to the
 * {@link Logger}'s {@link Logger.event} method.
 *
 * If the {@link AmplitudeEventContainer} is not provided to the {@link Logger} on instantiation,
 * it will be a required field on the context that is supplied to the {@link Logger}'s
 * {@link Logger.event} method.
 *
 * Usage with Hook
 * ---------------
 *
 * When the event method is called from a {@link Logger} instance that is instantiated via a hook,
 * the container, {@link AmplitudeEventContainer}, can be provided statically to the configuration
 * of the hook if it is in the same scope of the component in which that hook applies (i.e. the
 * render body of the component):
 *
 * Ex.)
 * const Tile = () => {
 *   const logger = useLogger({ container: "Saved Views"})
 *   ...
 * }
 *
 * In this case, the 'container' is no longer a required context argument to the 'logger.event'
 * method call, but can still be provided to the 'logger.event' method call if the statically
 * provided hook option should be overridden for that specific case:
 *
 * const Tile = () => {
 *   const logger = useLogger({ container: "Tile"})
 *   ...
 *   return (
 *     <EllipsisButton onClick={() => {
 *       ...
 *       logger.event.optionsMenuOpened({
 *         eventType: "click",
 *         eventDetails: {...},
 *       });
 *     }} />
 *   )
 * }
 */
export type AmplitudeEventContainer = EnumeratedLiteralType<
  typeof AmplitudeEventContainers
>;

export const AmplitudeNativeEventTypes = enumeratedLiterals(
  [
    "exit",
    "navigate",
    "double-click",
    "click",
    "open",
    "close",
    "change",
    "submit",
    "mount",
    "visit",
    "select",
    "apply-filters",
    "clear-filters",
    "back",
    "next",
  ] as const,
  {},
);

export type AmplitudeNativeEventType = EnumeratedLiteralType<
  typeof AmplitudeNativeEventTypes
>;

export const AmplitudeEventIds = enumeratedLiterals(
  [
    "export-sales-chart-data",
    "select-sales-saved-view",
    "select-overview-saved-view",
    "select-creative-analytics-saved-view",
    "create-sales-saved-view",
    "create-overview-saved-view",
    "create-creative-analytics-saved-view",

    // LTV page
    "customer-ltv-page-select-groupby",
    "customer-ltv-page-select-date-range",
    "customer-ltv-page-select-update-report",

    // Orders page
    "orders-page-select-platform",
    "orders-page-order-id",

    // Sales page
    "sales-page-update-breakdowns",
    "sales-page-export-scheduled",
    "sales-page-export-now",

    // Overview page
    "overview-page-add-tile",
    "overview-page-select-date-range-custom",
    "overview-page-select-date-range-predefined",
  ] as const,
  {},
);

export type AmplitudeEventId = EnumeratedLiteralType<typeof AmplitudeEventIds>;
